import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { RootState } from 'reducers/rootReducer';
import posed from 'react-pose';
import styled from '@emotion/styled';
import { ModalWallResize, ModalBackdrop } from 'components/modal/Modal';
import { positionedModal, fade } from 'components/animations';
import { sidebarItemButtonStyles } from 'components/sidebar/styles';
import { messages } from 'config/messages';
import { ReactComponent as IconClose } from 'assets/icons/x.svg';
import { css } from '@emotion/react';
import { colors } from 'config/paletteConfig';

import { selectors as unsavedModalSelectors, actions as unsavedModalActions } from 'ducks/modal/unsavedModal';
import { selectors as sketchPersistenceSelectors, actions as sketchPersistenceActions } from 'ducks/persistence/sketchPersistence';
import { selectors as orderDetailsSelectors } from 'ducks/orderDetails/orderDetails';
import { actions as saveSketchModalActions } from 'ducks/modal/saveSketchModal';
import { isWorkbenchApplication, doSketchExit } from 'effects/auth';

const Backdrop = styled(ModalBackdrop)({
  display: 'flex',
  flexFlow: 'column nowrap',
  alignItems: 'center',
  justifyContent: 'center',
});

const SaveAndQuitCss = css`
  color: white;
  background: ${colors.blue};
  margin: 3px;
  cursor: pointer;
  box-sizing: content-box;
  display: flex;
  align-items: center;
  height: 24px;
  border: 0;
  padding: 10px 15px;
  text-align: left;
  user-select: none;
  box-shadow: 4px 0 0 0 transparent inset;
  transition: box-shadow 0.15s linear;
`;

const QuitWithoutSavingCss = css`
  color: black;
  background: ${colors.white};
  margin: 3px;
  cursor: pointer;
  box-sizing: content-box;
  display: flex;
  align-items: center;
  height: 24px;
  border: 0;
  padding: 10px 15px;
  text-align: left;
  user-select: none;
  box-shadow: 4px 0 0 0 transparent inset;
  transition: box-shadow 0.15s linear;
`;

const BackdropAnimated = posed(Backdrop)(fade);
const ModalAnimated = posed(ModalWallResize)(positionedModal);
const CloseButton = styled.button(sidebarItemButtonStyles);

interface StateProps {
  readonly currentSketch: any;
  readonly isCreated: boolean;
  readonly isShowingUnsavedModal: boolean;
  readonly getOrderDetails: any
}

interface ActionProps {
  readonly hideUnsavedModal: () => void;
  readonly saveSketchToApi: (payload?: any) => void;
  readonly queueLogout: (payload?: any) => void;
  readonly showSaveSketchModal: (payload?: any) => void;
}

type Props = StateProps & ActionProps;

interface State {
  sketchName: string;
  address: string;
  orderId: string;
  isCreated: boolean;
}

class UnsavedModal extends Component<Props, State> {
  // eslint-disable-next-line react/sort-comp
  private readonly focusRef: React.RefObject<HTMLInputElement>;

  public constructor(props: Props) {
    super(props);
    this.focusRef = createRef();

    const allChangesSaved = !localStorage.getItem('isDirty');
    if (allChangesSaved) {
      this.doLogOut(null);
      return;
    }

    const { currentSketch, isCreated, getOrderDetails } = props;
    const sketchExists = isCreated ?? !!localStorage.getItem('currentSketch');
    const sketchName = (currentSketch?.sketch_name) ? currentSketch?.sketch_name : '';
    const address = (currentSketch?.property_address) ? currentSketch?.property_address : getOrderDetails.address;
    const id = (currentSketch?.order_id) ? currentSketch?.order_id : getOrderDetails.orderId;

    this.state = {
      sketchName,
      address,
      orderId: id,
      isCreated: sketchExists,
    };
  }

  private doClose = (): void => {
    const { hideUnsavedModal } = this.props;
    hideUnsavedModal();
  }

  private doLogOut = async (event?: React.MouseEvent<HTMLElement> | null): Promise<void> => {
    if (event) event.preventDefault();

    const { orderId } = this.state;
    // selects the Exit Path (Logout/Workbench Return) based on Sketch Mode
    doSketchExit(orderId);
  }

  private doSave = (
    event?: React.KeyboardEvent<HTMLInputElement> | React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>,
  ): void => {
    if (event) event.preventDefault();
    const { sketchName, address, orderId, isCreated } = this.state;
    const { saveSketchToApi, queueLogout, showSaveSketchModal } = this.props;

    // if created, overwrite the existing sketch
    if (isCreated) {
      // followUp = doLogout after we've successfully saved.
      saveSketchToApi({ sketchName, propertyAddress: address, orderId, followUp: this.doLogOut });
      return;
    }

    // if new workbench sketch, use generated sketchName; we will have an orderId and propertyAddress from the order.
    if (isWorkbenchApplication()) {
      const generatedSketchName = `${orderId}-Sketch`;
      saveSketchToApi({ sketchName: generatedSketchName, propertyAddress: address, orderId, followUp: this.doLogOut });
    } else { // otherwise, queue a Logout to return after the SaveSketchModal.
      queueLogout();
      showSaveSketchModal();
      this.doClose();
    }
  };

  public render = (): JSX.Element | null => {
    const cancelEvent = (event: React.PointerEvent | React.WheelEvent | React.DragEvent): void => {
      event.stopPropagation();
    };

    const Action = (isWorkbenchApplication()) ? 'Return' : 'Exit';
    const SaveText = `Save & ${Action}`;
    const ExitText = `${Action} without Saving`;

    return (
      <BackdropAnimated
        onPointerDown={cancelEvent}
        onPointerMove={cancelEvent}
        onPointerUp={cancelEvent}
        onPointerLeave={cancelEvent}
        onWheel={cancelEvent}
        onDragOver={cancelEvent}
        onDrop={cancelEvent}
        pose="enter"
        initialPose="exit"
      >
        <ModalAnimated
          pose="enter"
          initialPose="exit"
          position={window.screen.width > 400 ? { top: '15rem', left: '90%' } : { top: '15rem', left: '1rem' }}
          style={window.screen.width > 400 ? { width: '30%' } : { width: '85%' }}
        >
          <h2>
            <CloseButton onClick={this.doClose} title={messages.closeModal} style={{ cursor: 'pointer' }}>
              <IconClose />
            </CloseButton>
            <span>Unsaved Changes</span>
          </h2>
          <div>
            <div style={{ marginBottom: '10px', marginTop: '10px' }}>Would you like to save first?</div>
            <button onClick={this.doSave} css={SaveAndQuitCss} style={{ float: 'right', border: '1px solid black' }}>
              <span>{SaveText}</span>
            </button>
            <button onClick={this.doLogOut} css={QuitWithoutSavingCss} style={{ float: 'right', border: '1px solid black' }}>
              <span>{ExitText}</span>
            </button>
          </div>
        </ModalAnimated>
      </BackdropAnimated>
    );
  };
} // END OF CLASS

export default connect(
  (state: RootState): StateProps => ({
    isShowingUnsavedModal: unsavedModalSelectors.isShowing(state),
    currentSketch: sketchPersistenceSelectors.getSketch(state),
    isCreated: sketchPersistenceSelectors.isCreated(state),
    getOrderDetails: orderDetailsSelectors.getOrderDetails(state),
  }),
  {
    saveSketchToApi: sketchPersistenceActions.saveSketch,
    hideUnsavedModal: unsavedModalActions.hide,
    queueLogout: unsavedModalActions.queue,
    showSaveSketchModal: saveSketchModalActions.show,
  },
)(UnsavedModal);
